import { useState, useRef } from 'react';
import { Shield, Check, Upload, ChevronRight, AlertCircle } from 'lucide-react';
import { dealerRegister, dealerUpdateRegistration, dealerSubmitApplication, dealerUploadDocument } from '../services/api';

const STEPS = ['Account Info', 'Business Details', 'Licensing & Docs'];

export default function DealerSignup() {
  const [step, setStep] = useState(0);
  const [dealerId, setDealerId] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const fileRef = useRef();

  const [form, setForm] = useState({
    firstName: '', lastName: '', businessName: '', email: '', phone: '', password: '',
    businessAddress: '', city: '', state: '', zip: '', taxId: '',
    fflNumber: '', fflExpiry: '', sotStatus: 'no',
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors placeholder-zinc-600";
  const labelCls = "block font-inter text-xs text-zinc-500 uppercase tracking-widest mb-2";

  const handleStep1 = async () => {
    if (!form.firstName || !form.lastName || !form.businessName || !form.email || !form.password) {
      setError('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await dealerRegister({
        first_name: form.firstName, last_name: form.lastName,
        business_name: form.businessName, email: form.email,
        phone: form.phone, password: form.password,
      });
      setDealerId(res.dealer_id);
      setStep(1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async () => {
    if (!dealerId) { setError('Registration error. Please refresh.'); return; }
    setLoading(true);
    setError('');
    try {
      await dealerUpdateRegistration(dealerId, {
        business_address: form.businessAddress, city: form.city,
        state: form.state, zip_code: form.zip, tax_id: form.taxId,
      });
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!dealerId || !file) return;
    setLoading(true);
    try {
      const doc = await dealerUploadDocument(dealerId, file, 'ffl');
      setUploadedDocs(prev => [...prev, doc]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!dealerId) return;
    setLoading(true);
    setError('');
    try {
      // Save FFL details first
      await dealerUpdateRegistration(dealerId, {
        ffl_number: form.fflNumber, ffl_expiry: form.fflExpiry, sot_status: form.sotStatus,
      });
      // Submit application
      await dealerSubmitApplication(dealerId);
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-20 flex items-center justify-center px-6">
        <div data-testid="dealer-success" className="max-w-md w-full bg-[#111111] border border-green-500/30 p-12 text-center">
          <div className="w-16 h-16 bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <Check size={32} className="text-green-500" />
          </div>
          <h2 className="font-bebas text-4xl text-white tracking-wider mb-3">APPLICATION SUBMITTED!</h2>
          <p className="font-inter text-sm text-zinc-400 leading-relaxed mb-2">
            Thank you for applying to the Witt Machine dealer program.
          </p>
          <p className="font-inter text-sm text-zinc-400 leading-relaxed mb-6">
            We'll review your application and reach out within 2-3 business days. Once approved, you can log in at <strong className="text-orange-500">/dealer/login</strong>.
          </p>
          <div className="flex flex-col gap-3">
            <a href="/dealer/login" className="inline-block font-bebas text-sm tracking-widest bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 transition-colors">
              GO TO DEALER LOGIN
            </a>
            <a href="/" className="font-inter text-xs text-zinc-500 hover:text-white transition-colors">
              Return to homepage
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-16 lg:pt-20">
      <div className="bg-[#111111] border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-14">
          <div className="flex items-center gap-2 mb-4">
            <Shield size={16} className="text-orange-500" />
            <span className="font-inter text-xs text-orange-500 uppercase tracking-widest">Authorized Dealer Program</span>
          </div>
          <h1 data-testid="dealer-signup-heading" className="font-bebas text-5xl lg:text-6xl text-white tracking-wider mb-3">
            DEALER APPLICATION
          </h1>
          <p className="font-inter text-sm text-zinc-500">Apply for wholesale pricing and access the Witt Machine dealer portal.</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Step indicator */}
        <div className="flex items-center gap-0 mb-10">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center flex-1">
              <div className={`flex items-center gap-2 ${i <= step ? 'text-white' : 'text-zinc-600'}`}>
                <div className={`w-8 h-8 flex items-center justify-center text-sm font-bebas tracking-wider border ${
                  i < step ? 'bg-orange-500 border-orange-500 text-white' :
                  i === step ? 'border-orange-500 text-orange-500' :
                  'border-zinc-700 text-zinc-600'
                }`}>
                  {i < step ? <Check size={14} /> : i + 1}
                </div>
                <span className="font-inter text-xs hidden sm:block uppercase tracking-widest">{s}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-[1px] mx-3 ${i < step ? 'bg-orange-500' : 'bg-zinc-800'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="bg-[#111111] border border-white/10 p-8">
          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3 mb-5">
              <AlertCircle size={14} />{error}
            </div>
          )}

          {/* Step 0: Account Info */}
          {step === 0 && (
            <div data-testid="dealer-step-1">
              <h2 className="font-bebas text-2xl text-white tracking-wider mb-6">ACCOUNT INFORMATION</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>First Name *</label><input data-testid="dealer-first-name" className={inputCls} value={form.firstName} onChange={set('firstName')} placeholder="First name" /></div>
                <div><label className={labelCls}>Last Name *</label><input data-testid="dealer-last-name" className={inputCls} value={form.lastName} onChange={set('lastName')} placeholder="Last name" /></div>
                <div className="sm:col-span-2"><label className={labelCls}>Business Name *</label><input data-testid="dealer-business-name" className={inputCls} value={form.businessName} onChange={set('businessName')} placeholder="Your gun shop / business name" /></div>
                <div><label className={labelCls}>Email *</label><input data-testid="dealer-email" type="email" className={inputCls} value={form.email} onChange={set('email')} placeholder="you@gunshop.com" /></div>
                <div><label className={labelCls}>Phone</label><input data-testid="dealer-phone" type="tel" className={inputCls} value={form.phone} onChange={set('phone')} placeholder="(555) 555-5555" /></div>
                <div className="sm:col-span-2"><label className={labelCls}>Password *</label><input data-testid="dealer-password" type="password" className={inputCls} value={form.password} onChange={set('password')} placeholder="Create a password (min 8 chars)" /></div>
              </div>
            </div>
          )}

          {/* Step 1: Business details */}
          {step === 1 && (
            <div data-testid="dealer-step-2">
              <h2 className="font-bebas text-2xl text-white tracking-wider mb-6">BUSINESS DETAILS</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><label className={labelCls}>Business Address</label><input className={inputCls} value={form.businessAddress} onChange={set('businessAddress')} placeholder="123 Main Street" /></div>
                <div><label className={labelCls}>City</label><input className={inputCls} value={form.city} onChange={set('city')} placeholder="City" /></div>
                <div><label className={labelCls}>State</label><input className={inputCls} value={form.state} onChange={set('state')} placeholder="TX" /></div>
                <div><label className={labelCls}>ZIP Code</label><input className={inputCls} value={form.zip} onChange={set('zip')} placeholder="75001" /></div>
                <div><label className={labelCls}>Tax ID / EIN</label><input className={inputCls} value={form.taxId} onChange={set('taxId')} placeholder="XX-XXXXXXX" /></div>
              </div>
            </div>
          )}

          {/* Step 2: FFL / Documents */}
          {step === 2 && (
            <div data-testid="dealer-step-3">
              <h2 className="font-bebas text-2xl text-white tracking-wider mb-6">LICENSING & DOCUMENTATION</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>FFL License Number *</label><input data-testid="dealer-ffl" className={inputCls} value={form.fflNumber} onChange={set('fflNumber')} placeholder="1-XX-XXX-XX-XX-XXXXX" /></div>
                <div><label className={labelCls}>FFL Expiration Date</label><input type="date" className={inputCls} value={form.fflExpiry} onChange={set('fflExpiry')} /></div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>SOT Status</label>
                  <select className={inputCls} value={form.sotStatus} onChange={set('sotStatus')}>
                    <option value="no">No SOT</option>
                    <option value="yes">Yes — SOT Holder</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>FFL Document Upload</label>
                  <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => e.target.files[0] && handleFileUpload(e.target.files[0])} />
                  <div
                    className="border border-dashed border-white/20 hover:border-orange-500/50 p-8 text-center cursor-pointer transition-colors"
                    onClick={() => fileRef.current?.click()}
                  >
                    <Upload size={24} className="text-zinc-600 mx-auto mb-2" />
                    <p className="font-inter text-sm text-zinc-500">{loading ? 'Uploading...' : 'Click to upload FFL copy (PDF, JPG, PNG)'}</p>
                    <p className="font-inter text-xs text-zinc-700 mt-1">Required for approval</p>
                  </div>
                  {uploadedDocs.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {uploadedDocs.map((d, i) => (
                        <div key={i} className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 px-3 py-2">
                          <Check size={13} className="text-green-500" />
                          <span className="font-inter text-xs text-green-400">{d.original_name} uploaded</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-6 bg-[#1a1a1a] border border-white/5 p-4">
                <p className="font-inter text-xs text-zinc-500 leading-relaxed">
                  By submitting this application you agree to the Witt Machine Dealer Agreement. We will review your application within 2-3 business days.
                </p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
            <button
              onClick={() => { setStep(s => Math.max(0, s - 1)); setError(''); }}
              disabled={step === 0 || loading}
              className="font-bebas text-sm tracking-widest text-zinc-500 hover:text-white transition-colors disabled:opacity-30"
            >
              ← BACK
            </button>
            {step === 0 && (
              <button
                data-testid="dealer-next-step"
                onClick={handleStep1}
                disabled={loading}
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-sm tracking-widest px-6 py-3 transition-colors"
              >
                {loading ? 'SAVING...' : 'NEXT STEP'} <ChevronRight size={16} />
              </button>
            )}
            {step === 1 && (
              <button
                data-testid="dealer-next-step"
                onClick={handleStep2}
                disabled={loading}
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-sm tracking-widest px-6 py-3 transition-colors"
              >
                {loading ? 'SAVING...' : 'NEXT STEP'} <ChevronRight size={16} />
              </button>
            )}
            {step === 2 && (
              <button
                data-testid="dealer-submit"
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-sm tracking-widest px-6 py-3 transition-colors"
              >
                {loading ? 'SUBMITTING...' : 'SUBMIT APPLICATION'} <Check size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
